/**
 * Result export.
 *
 * CSV and JSON are produced in the browser from the cached rows -- instant, and
 * correct for wide integers because every value goes through `renderableText`.
 * Parquet needs the engine, so it is generated by DuckDB-WASM when the result is
 * cached, and by the server otherwise.
 */

import { renderableText, stringifyJson } from '@/lib/values';
import { quoteIdent } from '@/lib/values';
import { sqlString } from '@/cache/sqlgen';
import type { ColumnSchema, ResultRow } from '@/types/api';

export type ExportFormat = 'csv' | 'json' | 'parquet';

export function toCsv(columns: readonly ColumnSchema[], rows: readonly ResultRow[]): string {
  const escape = (text: string): string =>
    /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;

  const lines: string[] = [columns.map((column) => escape(column.name)).join(',')];

  for (const row of rows) {
    const cells = columns.map((_, index) => {
      const value = row[index];
      // An unquoted empty field is NULL in CSV; "" is an empty string. Keeping
      // them distinct here matters as much as it does in the grid.
      if (value === null || value === undefined) return '';
      const text = renderableText(value);
      return text === '' ? '""' : escape(text);
    });
    lines.push(cells.join(','));
  }

  return lines.join('\r\n');
}

export function toJson(columns: readonly ColumnSchema[], rows: readonly ResultRow[]): string {
  const records = rows.map((row) => {
    const record: Record<string, unknown> = {};
    columns.forEach((column, index) => {
      const value = row[index];
      record[column.name] =
        typeof value === 'bigint' ? value.toString() : (value ?? null);
    });
    return record;
  });
  return stringifyJson(records, 2);
}

export function download(filename: string, data: BlobPart, mime: string): void {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoke on the next tick so Firefox has started the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `COPY (...) TO '...' (FORMAT PARQUET)` against the local cache table. */
export function copyToParquetSql(table: string, path: string): string {
  return `COPY (SELECT * FROM ${quoteIdent(table)}) TO ${sqlString(path)} (FORMAT PARQUET, COMPRESSION zstd)`;
}

export function suggestedFilename(format: ExportFormat): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `result-${stamp}.${format}`;
}
